import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Crosshair, Telescope, Wind, Ruler, Save, Trash2, RotateCcw } from "lucide-react";
import { useCalculatorStore } from "@/store/calculatorStore";
import { CONVERT } from "@/utils/ballisticsMath";
import { CLICK_OPTIONS } from "@/utils/units";
import { useT } from "@/i18n/translations";
import { Slider } from "@/components/ui/slider";
import WindDial from "@/components/calculator/WindDial";
import pelletsData from "@/data/pellets.json";

const fmtNum = (v) => {
  if (v === null || v === undefined || Number.isNaN(v)) return "";
  const r = Math.round(v * 10000) / 10000;
  return String(r);
};

const NumberField = ({ label, value, onCommit, unit, testId, step = "any", min }) => {
  const [text, setText] = useState(fmtNum(value));
  const committedRef = useRef(value);

  useEffect(() => {
    if (Math.abs((value ?? 0) - (committedRef.current ?? 0)) > 1e-6) {
      committedRef.current = value;
      setText(fmtNum(value));
    }
  }, [value]);

  const handle = (e) => {
    const tval = e.target.value;
    setText(tval);
    const n = parseFloat(tval);
    if (!Number.isNaN(n)) {
      committedRef.current = n;
      onCommit(n);
    }
  };

  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-zinc-400">{label}</span>
      <div className="flex items-center rounded-md border border-dase-border bg-dase-input focus-within:border-dase-red/70">
        <input
          type="number"
          inputMode="decimal"
          step={step}
          min={min}
          value={text}
          onChange={handle}
          data-testid={testId}
          className="w-full bg-transparent px-3 py-2 font-mono text-sm text-zinc-100 outline-none placeholder:text-zinc-600"
        />
        {unit && <span className="px-3 text-[10px] font-bold uppercase tracking-wide text-zinc-500">{unit}</span>}
      </div>
    </label>
  );
};

const SliderField = ({ label, value, display, unit, min, max, step, onChange, testId }) => (
  <div>
    <div className="mb-2 flex items-baseline justify-between">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">{label}</span>
      <span className="font-mono text-sm font-bold text-zinc-100">
        {display}
        <span className="ml-1 text-[10px] font-bold text-dase-red">{unit}</span>
      </span>
    </div>
    <Slider
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={(v) => onChange(v[0])}
      data-testid={testId}
    />
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div className="rounded-md border border-dase-border bg-dase-panel p-4">
    <div className="mb-4 flex items-center gap-2 border-b border-dase-border pb-2">
      <Icon className="h-4 w-4 text-dase-red" strokeWidth={2.2} />
      <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-200">{title}</h2>
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const Segmented = ({ options, value, onChange, testIdPrefix }) => (
  <div className="grid grid-cols-4 gap-1 rounded-md border border-dase-border bg-dase-input p-1">
    {options.map((o) => (
      <button
        key={o.id}
        data-testid={`${testIdPrefix}-${o.id}`}
        onClick={() => onChange(o.id)}
        className={`rounded py-1.5 text-[11px] font-bold uppercase tracking-wide transition-colors ${
          value === o.id ? "bg-dase-red text-white" : "text-zinc-400 hover:text-zinc-200"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export const InputSidebar = () => {
  const t = useT();
  const s = useCalculatorStore();
  const imperial = s.unitSystem === "imperial";
  const [profileName, setProfileName] = useState("");

  const onSelectPellet = (e) => {
    const id = e.target.value;
    for (const cal of pelletsData.calibers) {
      const p = cal.pellets.find((x) => x.id === id);
      if (p) {
        s.selectPellet(p);
        toast.success(`${t("toast.pelletSelected")}: ${p.name}`, {
          description: `${p.weight_g} g · ${p.weight_gr} gr · BC ${p.bc}`,
        });
        return;
      }
    }
  };

  const handleSave = () => {
    const name = profileName.trim() || `${t("input.profiles")} ${s.savedProfiles.length + 1}`;
    s.saveProfile(name);
    setProfileName("");
    toast.success(`${t("toast.profileSaved")}: ${name}`);
  };

  // Display <-> SI converters
  const massDisp = imperial ? CONVERT.gToGr(s.massKg * 1000) : s.massKg * 1000;
  const v0Disp = imperial ? CONVERT.msToFps(s.v0) : s.v0;
  const zeroDisp = imperial ? CONVERT.mToYd(s.zeroDistance) : s.zeroDistance;
  const tgtDisp = imperial ? CONVERT.mToYd(s.targetDistance) : s.targetDistance;
  const scopeDisp = imperial ? CONVERT.mToIn(s.sightHeight) : s.sightHeight * 100;
  const windDisp = imperial ? CONVERT.msToMph(s.windSpeed) : s.windSpeed;
  const tempDisp = imperial ? CONVERT.cToF(s.tempC) : s.tempC;
  const altDisp = imperial ? s.altitude * 3.28084 : s.altitude;

  const holdoverOpts = [
    { id: "cm", label: "CM" },
    { id: "inch", label: "IN" },
    { id: "mrad", label: "MRAD" },
    { id: "moa", label: "MOA" },
  ];

  return (
    <aside className="space-y-4" data-testid="input-sidebar">
      <Section icon={Crosshair} title={t("input.pelletProfile")}>
        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
            {t("input.selectPellet")}
          </span>
          <select
            value={s.selectedPelletId}
            onChange={onSelectPellet}
            data-testid="pellet-select"
            className="w-full rounded-md border border-dase-border bg-dase-input px-3 py-2 text-sm text-zinc-100 outline-none focus:border-dase-red/70"
          >
            {pelletsData.calibers.map((cal) => (
              <optgroup key={cal.caliber} label={cal.caliber}>
                {cal.pellets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {p.weight_g}g / BC {p.bc}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <NumberField
            label={t("input.mass")}
            value={massDisp}
            unit={imperial ? "gr" : "g"}
            testId="input-mass"
            onCommit={(n) => s.setField("massKg", (imperial ? CONVERT.grToG(n) : n) / 1000)}
          />
          <NumberField
            label={t("input.v0")}
            value={v0Disp}
            unit={imperial ? "fps" : "m/s"}
            testId="input-v0"
            onCommit={(n) => s.setField("v0", imperial ? CONVERT.fpsToMs(n) : n)}
          />
        </div>
        <NumberField
          label={t("input.bc")}
          value={s.bc}
          unit="lb/in²"
          step="0.001"
          testId="input-bc"
          onCommit={(n) => s.setField("bc", n)}
        />
      </Section>

      <Section icon={Telescope} title={t("input.opticsZero")}>
        <SliderField
          label={t("input.targetDistance")}
          value={Math.round(tgtDisp)}
          display={Math.round(tgtDisp)}
          unit={imperial ? "yd" : "m"}
          min={5}
          max={imperial ? 160 : 150}
          step={5}
          onChange={(v) => s.setField("targetDistance", imperial ? CONVERT.ydToM(v) : v)}
          testId="input-target-distance"
        />
        <div className="grid grid-cols-2 gap-3">
          <NumberField
            label={t("input.zeroDistance")}
            value={zeroDisp}
            unit={imperial ? "yd" : "m"}
            testId="input-zero-distance"
            onCommit={(n) => s.setField("zeroDistance", imperial ? CONVERT.ydToM(n) : n)}
          />
          <NumberField
            label={t("input.sightHeight")}
            value={scopeDisp}
            unit={imperial ? "in" : "cm"}
            testId="input-sight-height"
            onCommit={(n) => s.setField("sightHeight", imperial ? CONVERT.inToM(n) : n / 100)}
          />
        </div>
        <NumberField
          label={t("input.inclination")}
          value={s.inclination}
          unit="°"
          testId="input-inclination"
          onCommit={(n) => s.setField("inclination", n)}
        />
      </Section>

      <Section icon={Wind} title={t("input.windEnv")}>
        <SliderField
          label={t("input.windSpeed")}
          value={Number(windDisp.toFixed(1))}
          display={windDisp.toFixed(1)}
          unit={imperial ? "mph" : "m/s"}
          min={0}
          max={imperial ? 35 : 15}
          step={imperial ? 1 : 0.5}
          onChange={(v) => s.setField("windSpeed", imperial ? CONVERT.mphToMs(v) : v)}
          testId="input-wind-speed"
        />
        <div className="flex flex-col items-center pt-1">
          <span className="mb-1 self-start text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
            {t("input.windDir")}
          </span>
          <WindDial value={s.windDir} onChange={(deg) => s.setField("windDir", deg)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumberField
            label={t("input.temp")}
            value={tempDisp}
            unit={imperial ? "°F" : "°C"}
            testId="input-temp"
            onCommit={(n) => s.setField("tempC", imperial ? CONVERT.fToC(n) : n)}
          />
          <NumberField
            label={t("input.altitude")}
            value={altDisp}
            unit={imperial ? "ft" : "m"}
            testId="input-altitude"
            onCommit={(n) => s.setField("altitude", imperial ? n / 3.28084 : n)}
          />
        </div>
      </Section>

      <Section icon={Ruler} title={t("input.holdoverUnit")}>
        <Segmented options={holdoverOpts} value={s.holdoverUnit} onChange={s.setHoldoverUnit} testIdPrefix="holdover-unit" />
        <label className="block">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
            {t("input.clickValue")}
          </span>
          <select
            value={s.clickId}
            onChange={(e) => {
              const opt = CLICK_OPTIONS.find((o) => o.id === e.target.value);
              s.setClick(opt.id, opt.mrad);
            }}
            data-testid="click-value-select"
            className="w-full rounded-md border border-dase-border bg-dase-input px-3 py-2 text-sm text-zinc-100 outline-none focus:border-dase-red/70"
          >
            {CLICK_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </Section>

      <Section icon={Save} title={t("input.profiles")}>
        <div className="flex gap-2">
          <input
            type="text"
            value={profileName}
            onChange={(e) => setProfileName(e.target.value)}
            placeholder={t("input.profileName")}
            data-testid="profile-name-input"
            className="w-full rounded-md border border-dase-border bg-dase-input px-3 py-2 text-sm text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-dase-red/70"
          />
          <button
            onClick={handleSave}
            data-testid="save-profile-btn"
            className="flex items-center gap-1 rounded-md bg-dase-red px-3 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-dase-red-bright"
          >
            <Save className="h-3.5 w-3.5" /> {t("input.save")}
          </button>
        </div>
        {s.savedProfiles.length > 0 && (
          <div className="space-y-1.5" data-testid="saved-profiles-list">
            {s.savedProfiles.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded border border-dase-border bg-dase-input px-3 py-2">
                <span className="truncate text-xs font-medium text-zinc-200">{p.name}</span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      s.loadProfile(p.id);
                      toast.success(`${t("toast.loaded")}: ${p.name}`);
                    }}
                    data-testid={`load-profile-${p.id}`}
                    className="rounded px-2 py-1 text-[10px] font-bold uppercase text-dase-red hover:bg-dase-red/10"
                  >
                    {t("input.load")}
                  </button>
                  <button
                    onClick={() => s.deleteProfile(p.id)}
                    data-testid={`delete-profile-${p.id}`}
                    className="rounded p-1 text-zinc-500 hover:text-dase-red"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <button
          onClick={() => {
            s.resetInputs();
            toast(t("toast.reset"));
          }}
          data-testid="reset-btn"
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dase-border py-2 text-xs font-bold uppercase tracking-wide text-zinc-400 transition-colors hover:border-zinc-600 hover:text-zinc-200"
        >
          <RotateCcw className="h-3.5 w-3.5" /> {t("input.reset")}
        </button>
      </Section>
    </aside>
  );
};

export default InputSidebar;
